import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { WORLD_CONFIG } from '@astra/shared';

/**
 * ConstellationBoundaryWarning - Visual warning effect at constellation edges
 *
 * Creates glowing walls positioned at the actual world-space constellation boundaries.
 * The walls are fixed in world space and become visible as the player approaches.
 */
export class ConstellationBoundaryWarning extends Container {
  // Constellation bounds
  private bounds: { minX: number; maxX: number; minY: number; maxY: number };

  // Boundary wall graphics (world-positioned)
  private leftWall!: Graphics;
  private rightWall!: Graphics;
  private topWall!: Graphics;
  private bottomWall!: Graphics;

  // Warning texts (world-positioned near walls)
  private leftText!: Text;
  private rightText!: Text;
  private topText!: Text;
  private bottomText!: Text;

  private warningPulseTime: number = 0;

  // Configuration
  private readonly WARNING_DISTANCE = 1000;  // Start showing wall at this distance
  private readonly MAX_ALPHA = 0.8;           // Maximum glow intensity
  private readonly WALL_WIDTH = 200;          // Width of gradient wall
  private readonly GLOW_DEPTH = 5;            // Behind most gameplay elements
  private readonly TEXT_THRESHOLD = 400;      // Show text when this close

  // Colors - using orange/red for danger feeling
  private readonly GLOW_COLOR = 0xff4444;     // Red glow
  private readonly TEXT_COLOR = 0xff6644;    // Orange-red text

  constructor(_constellationX: number, _constellationY: number, constellationSize: number = WORLD_CONFIG.CONSTELLATION_SIZE) {
    super();

    // Constellation bounds — LOCAL coordinates, constellation is centered at (0,0).
    // constellationSize is the FULL physical size (mirrors WORLD_CONFIG.CONSTELLATION_SIZE /
    // GameState.constellationSize). Pass the per-constellation override so the tutorial's
    // 50,000-unit constellation renders the boundary at ±25,000 instead of ±450,000.
    const halfSize = constellationSize / 2;
    this.bounds = {
      minX: -halfSize,
      maxX: halfSize,
      minY: -halfSize,
      maxY: halfSize,
    };

    this.createBoundaryWalls();
    this.createWarningTexts();

    // Set depth for this container
    this.zIndex = this.GLOW_DEPTH;
  }

  private createBoundaryWalls(): void {
    // Create graphics for each boundary wall
    this.leftWall = new Graphics();
    this.rightWall = new Graphics();
    this.topWall = new Graphics();
    this.bottomWall = new Graphics();

    // Set all to invisible initially
    [this.leftWall, this.rightWall, this.topWall, this.bottomWall].forEach(wall => {
      wall.alpha = 0;
      this.addChild(wall);
    });

    // Draw the wall gradients
    this.drawBoundaryWalls();
  }

  private drawBoundaryWalls(): void {
    const wallWidth = this.WALL_WIDTH;

    // Calculate wall dimensions - walls should NOT overlap at corners
    // Left/Right walls span from minY to maxY (vertical walls)
    // Top/Bottom walls span from minX to maxX (horizontal walls)
    const constellationHeight = this.bounds.maxY - this.bounds.minY;
    const constellationWidth = this.bounds.maxX - this.bounds.minX;

    const halfConstellationHeight = constellationHeight / 2;
    const halfConstellationWidth = constellationWidth / 2;

    // Left wall - positioned at bounds.minX, gradient fades to the right (into constellation)
    // Height spans from minY to maxY (full constellation height)
    this.leftWall.clear();
    for (let i = 0; i < wallWidth; i++) {
      // Alpha is highest at the boundary edge (i=0), fading inward
      const alpha = (1 - i / wallWidth) * 0.8;
      this.leftWall.rect(i, -halfConstellationHeight, 1, constellationHeight);
      this.leftWall.fill({ color: this.GLOW_COLOR, alpha });
    }
    this.leftWall.position.set(this.bounds.minX, (this.bounds.minY + this.bounds.maxY) / 2);

    // Right wall - positioned at bounds.maxX, gradient fades to the left (into constellation)
    this.rightWall.clear();
    for (let i = 0; i < wallWidth; i++) {
      // Alpha is highest at the boundary edge (i=wallWidth-1), fading inward
      const alpha = (i / wallWidth) * 0.8;
      this.rightWall.rect(-wallWidth + i, -halfConstellationHeight, 1, constellationHeight);
      this.rightWall.fill({ color: this.GLOW_COLOR, alpha });
    }
    this.rightWall.position.set(this.bounds.maxX, (this.bounds.minY + this.bounds.maxY) / 2);

    // Top wall - positioned at bounds.minY, gradient fades downward (into constellation)
    // Width spans from minX to maxX (full constellation width)
    this.topWall.clear();
    for (let i = 0; i < wallWidth; i++) {
      const alpha = (1 - i / wallWidth) * 0.8;
      this.topWall.rect(-halfConstellationWidth, i, constellationWidth, 1);
      this.topWall.fill({ color: this.GLOW_COLOR, alpha });
    }
    this.topWall.position.set((this.bounds.minX + this.bounds.maxX) / 2, this.bounds.minY);

    // Bottom wall - positioned at bounds.maxY, gradient fades upward (into constellation)
    this.bottomWall.clear();
    for (let i = 0; i < wallWidth; i++) {
      const alpha = (i / wallWidth) * 0.8;
      this.bottomWall.rect(-halfConstellationWidth, -wallWidth + i, constellationWidth, 1);
      this.bottomWall.fill({ color: this.GLOW_COLOR, alpha });
    }
    this.bottomWall.position.set((this.bounds.minX + this.bounds.maxX) / 2, this.bounds.maxY);
  }

  private createWarningTexts(): void {
    const textStyle: TextStyle = new TextStyle({
      fontFamily: 'monospace',
      fontSize: 24,
      fill: this.TEXT_COLOR,
      stroke: { color: 0x000000, width: 3 },
      dropShadow: {
        color: 0xff0000,
        blur: 10,
        distance: 0,
      },
    });

    // Create text for each boundary (positioned in world space)
    this.leftText = new Text({ text: 'CONSTELLATION BOUNDARY', style: textStyle });
    this.leftText.anchor.set(0.5, 0.5);
    this.leftText.rotation = Math.PI / 2;  // Rotated to read along the wall
    this.leftText.position.set(this.bounds.minX + 120, 0);
    this.leftText.visible = false;
    this.addChild(this.leftText);

    this.rightText = new Text({ text: 'CONSTELLATION BOUNDARY', style: textStyle });
    this.rightText.anchor.set(0.5, 0.5);
    this.rightText.rotation = -Math.PI / 2;
    this.rightText.position.set(this.bounds.maxX - 120, 0);
    this.rightText.visible = false;
    this.addChild(this.rightText);

    this.topText = new Text({ text: 'CONSTELLATION BOUNDARY', style: textStyle });
    this.topText.anchor.set(0.5, 0.5);
    this.topText.position.set(0, this.bounds.minY + 120);
    this.topText.visible = false;
    this.addChild(this.topText);

    this.bottomText = new Text({ text: 'CONSTELLATION BOUNDARY', style: textStyle });
    this.bottomText.anchor.set(0.5, 0.5);
    this.bottomText.position.set(0, this.bounds.maxY - 120);
    this.bottomText.visible = false;
    this.addChild(this.bottomText);
  }

  /**
   * Update the warning effect based on player position
   * Call this every frame from GameScene.update()
   */
  update(playerX: number, playerY: number, delta: number): void {
    // Calculate distance to each edge
    const distToLeft = playerX - this.bounds.minX;
    const distToRight = this.bounds.maxX - playerX;
    const distToTop = playerY - this.bounds.minY;
    const distToBottom = this.bounds.maxY - playerY;

    // Calculate alpha for each wall based on distance
    // Wall becomes visible as player approaches
    const leftAlpha = Math.max(0, (1 - distToLeft / this.WARNING_DISTANCE)) * this.MAX_ALPHA;
    const rightAlpha = Math.max(0, (1 - distToRight / this.WARNING_DISTANCE)) * this.MAX_ALPHA;
    const topAlpha = Math.max(0, (1 - distToTop / this.WARNING_DISTANCE)) * this.MAX_ALPHA;
    const bottomAlpha = Math.max(0, (1 - distToBottom / this.WARNING_DISTANCE)) * this.MAX_ALPHA;

    // Apply alpha to walls
    this.leftWall.alpha = leftAlpha;
    this.rightWall.alpha = rightAlpha;
    this.topWall.alpha = topAlpha;
    this.bottomWall.alpha = bottomAlpha;

    // Update warning pulse
    this.warningPulseTime += delta;
    const pulse = 0.5 + Math.sin(this.warningPulseTime / 200) * 0.5;

    // Update text visibility and position based on player proximity
    // Left text
    if (distToLeft < this.TEXT_THRESHOLD) {
      this.leftText.visible = true;
      this.leftText.y = playerY;  // Follow player vertically
      this.leftText.alpha = pulse;
    } else {
      this.leftText.visible = false;
    }

    // Right text
    if (distToRight < this.TEXT_THRESHOLD) {
      this.rightText.visible = true;
      this.rightText.y = playerY;
      this.rightText.alpha = pulse;
    } else {
      this.rightText.visible = false;
    }

    // Top text
    if (distToTop < this.TEXT_THRESHOLD) {
      this.topText.visible = true;
      this.topText.x = playerX;  // Follow player horizontally
      this.topText.alpha = pulse;
    } else {
      this.topText.visible = false;
    }

    // Bottom text
    if (distToBottom < this.TEXT_THRESHOLD) {
      this.bottomText.visible = true;
      this.bottomText.x = playerX;
      this.bottomText.alpha = pulse;
    } else {
      this.bottomText.visible = false;
    }
  }

  /**
   * Handle viewport resize - walls are world-positioned so no resize needed
   * But we keep this method for API consistency
   */
  resize(_width: number, _height: number): void {
    // World-positioned walls don't need resizing
    // They're always tall/wide enough to cover the viewport
  }

  /**
   * Get the constellation bounds for external use (e.g., for physics clamping)
   */
  getConstellationBounds(): { minX: number; maxX: number; minY: number; maxY: number } {
    return { ...this.bounds };
  }

  /**
   * Update constellation coordinates and physical size (for when player changes constellations).
   * constellationSize is the FULL physical size in world units.
   */
  setConstellation(_constellationX: number, _constellationY: number, constellationSize: number = WORLD_CONFIG.CONSTELLATION_SIZE): void {
    // Constellation bounds — LOCAL coordinates, constellation is centered at (0,0).
    const halfSize = constellationSize / 2;
    this.bounds = {
      minX: -halfSize,
      maxX: halfSize,
      minY: -halfSize,
      maxY: halfSize,
    };

    // Redraw walls at new positions
    this.drawBoundaryWalls();

    // Reposition texts
    this.leftText.x = this.bounds.minX + 120;
    this.rightText.x = this.bounds.maxX - 120;
    this.topText.y = this.bounds.minY + 120;
    this.bottomText.y = this.bounds.maxY - 120;
  }

  destroy(): void {
    this.leftWall.destroy();
    this.rightWall.destroy();
    this.topWall.destroy();
    this.bottomWall.destroy();
    this.leftText.destroy();
    this.rightText.destroy();
    this.topText.destroy();
    this.bottomText.destroy();
    super.destroy();
  }
}
